/**
 * Better Auth product client (`@/features/auth/lib/auth-client`).
 *
 * The client half of the net-new product-owned auth surface (goal 046). The
 * `/sign-in`, `/sign-up`, `/forgot-password`, `/reset-password`,
 * `/verify-email`, and `/verify-phone` pages call these methods directly. It
 * is composed with the magic-link, email-OTP, phone-number, and passkey
 * client plugins so the passwordless, phone-verification (OTP.com via the
 * auth Worker), and passkey flows resolve.
 *
 * These pages ship DARK: BetterAuth is still the live provider, and the `/api/auth/*`
 * rewrite that backs this client is wired in goal 047. Until then the client is
 * same-origin against the default `/api/auth` basePath and its calls are inert.
 */
import { passkeyClient } from '@better-auth/passkey/client'
import { emailOTPClient, magicLinkClient, phoneNumberClient } from 'better-auth/client/plugins'
import { createAuthClient } from 'better-auth/react'

/**
 * Where new users land after sign-up, magic-link, or social sign-in. The
 * sign-in analytics idempotency guard in `CustomerLayout` keys off this exact
 * query string (`?signed-up=1`), so keep it stable.
 */
export const NEW_USER_CALLBACK_URL = '/onboarding?signed-up=1'

/**
 * Local-dev-only override that points the client at a `wrangler dev` auth Worker
 * on a different origin. It MUST be a full URL. Deployed builds leave it unset so
 * the client stays same-origin on Better Auth's default `/api/auth` basePath —
 * the only configuration that works unchanged across prod, staging, and preview
 * hosts. Never pass a `baseURL` in deployed builds.
 */
const baseURL = process.env.VITE_BETTER_AUTH_URL

export const authClient = createAuthClient({
  ...(baseURL ? { baseURL } : {}),
  plugins: [magicLinkClient(), emailOTPClient(), phoneNumberClient(), passkeyClient()],
})

/**
 * Structural alias for the composed client. Lets tests and UI type a mocked
 * client without reaching into Better Auth's internal generics.
 */
export type ReactAuthClientLike = typeof authClient
